import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  Target, 
  Clock, 
  Zap, 
  Award,
  Users,
  TrendingUp,
  Calendar,
  Globe
} from 'lucide-react';
import { format, subDays, differenceInDays } from 'date-fns';

const DetailedInsights: React.FC = () => {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['detailed-insights'],
    queryFn: async () => {
      const thirtyDaysAgo = subDays(new Date(), 30);
      const sevenDaysAgo = subDays(new Date(), 7);

      // Get comprehensive analytics data
      const [
        { data: allGenerations },
        { data: recentAnalytics },
        { data: weeklyAnalytics },
        { data: publicGenerations },
        { data: userProfiles }
      ] = await Promise.all([
        supabase
          .from('generations')
          .select('*'),
        supabase
          .from('generation_analytics')
          .select('*')
          .gte('created_at', thirtyDaysAgo.toISOString()),
        supabase
          .from('generation_analytics')
          .select('*')
          .gte('created_at', sevenDaysAgo.toISOString()),
        supabase
          .from('generations')
          .select('*')
          .eq('is_public', true),
        supabase
          .from('profiles')
          .select('*')
      ]);

      // Calculate key metrics
      const totalViews = allGenerations?.reduce((sum, g) => sum + (g.view_count || 0), 0) || 0;
      const totalShares = allGenerations?.reduce((sum, g) => sum + (g.share_count || 0), 0) || 0;
      const avgViewsPerGeneration = allGenerations?.length ? totalViews / allGenerations.length : 0;
      
      // Engagement rate calculation
      const engagementRate = totalViews > 0 ? (totalShares / totalViews) * 100 : 0;
      
      // Public content ratio
      const publicRatio = allGenerations?.length ? (publicGenerations?.length || 0) / allGenerations.length * 100 : 0;
      
      // User activity metrics
      const activeUsers = new Set(recentAnalytics?.map(a => a.user_id).filter(Boolean)).size;
      const weeklyActiveUsers = new Set(weeklyAnalytics?.map(a => a.user_id).filter(Boolean)).size;
      const totalUsers = userProfiles?.length || 0;
      const userActivationRate = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;
      
      // Growth metrics
      const recentGenerations = allGenerations?.filter(g => 
        new Date(g.created_at) >= thirtyDaysAgo
      ) || [];
      const weeklyGenerations = allGenerations?.filter(g => 
        new Date(g.created_at) >= sevenDaysAgo
      ) || [];
      
      // Content freshness
      const avgDaysSinceCreation = allGenerations?.length 
        ? allGenerations.reduce((sum, g) => sum + differenceInDays(new Date(), new Date(g.created_at)), 0) / allGenerations.length
        : 0;
      
      // Popular content types
      const typeDistribution = allGenerations?.reduce((acc, gen) => {
        acc[gen.generation_type] = (acc[gen.generation_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      
      const mostPopularType = Object.entries(typeDistribution)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || 'survey';

      // Peak usage analysis
      const hourlyDistribution = recentAnalytics?.reduce((acc, event) => {
        const hour = new Date(event.created_at).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      }, {} as Record<number, number>) || {};
      
      const peakHour = Object.entries(hourlyDistribution)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || '14';

      return {
        totalViews,
        totalShares,
        avgViewsPerGeneration,
        engagementRate,
        publicRatio,
        activeUsers,
        weeklyActiveUsers,
        totalUsers,
        userActivationRate,
        recentGenerations: recentGenerations.length,
        weeklyGenerations: weeklyGenerations.length,
        avgDaysSinceCreation,
        mostPopularType,
        peakHour: parseInt(peakHour),
        totalGenerations: allGenerations?.length || 0
      };
    }
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-muted rounded w-2/3"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
                <div className="h-2 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Platform Insights</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Engagement Health */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-4 h-4" />
              Engagement Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Engagement Rate</span>
                  <span className="font-medium">{insights?.engagementRate.toFixed(1)}%</span>
                </div>
                <Progress value={insights?.engagementRate} className="h-2" />
              </div>
              <div className="text-xs text-muted-foreground">
                {insights?.totalShares} shares from {insights?.totalViews} views
              </div>
              <Badge variant={insights?.engagementRate > 5 ? "default" : "secondary"}>
                {insights?.engagementRate > 5 ? "Healthy" : "Needs Improvement"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* User Activation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4" />
              User Activation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Active Users (30d)</span>
                  <span className="font-medium">{insights?.userActivationRate.toFixed(1)}%</span>
                </div>
                <Progress value={insights?.userActivationRate} className="h-2" />
              </div>
              <div className="text-xs text-muted-foreground">
                {insights?.activeUsers} of {insights?.totalUsers} registered users
              </div>
              <Badge variant={insights?.userActivationRate > 40 ? "default" : "secondary"}>
                {insights?.userActivationRate > 40 ? "Strong" : "Growing"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Content Velocity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="w-4 h-4" />
              Content Velocity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">This week</span>
                  <p className="font-medium">{insights?.weeklyGenerations}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">This month</span>
                  <p className="font-medium">{insights?.recentGenerations}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Avg: {(insights?.recentGenerations / 30).toFixed(1)} per day
              </div>
              <Badge variant="default">
                Trending {insights?.weeklyGenerations > 5 ? "Up" : "Steady"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Content Quality */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Award className="w-4 h-4" />
              Content Quality
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Avg Views/Generation</span>
                  <span className="font-medium">{insights?.avgViewsPerGeneration.toFixed(1)}</span>
                </div>
                <Progress value={Math.min(insights?.avgViewsPerGeneration * 10, 100)} className="h-2" />
              </div>
              <div className="text-xs text-muted-foreground">
                {insights?.publicRatio.toFixed(1)}% content is public
              </div>
              <Badge variant={insights?.avgViewsPerGeneration > 10 ? "default" : "secondary"}>
                {insights?.avgViewsPerGeneration > 10 ? "High Impact" : "Building Audience"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Platform Usage Patterns */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4" />
              Usage Patterns
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-muted-foreground">Peak Hour:</span>
                <p className="font-medium">
                  {format(new Date().setHours(insights?.peakHour || 14), 'h:mm a')}
                </p>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Popular Type:</span>
                <p className="font-medium capitalize">{insights?.mostPopularType}</p>
              </div>
              <div className="text-xs text-muted-foreground">
                Avg content age: {insights?.avgDaysSinceCreation.toFixed(0)} days
              </div>
              <Badge variant="outline">
                Weekly Active: {insights?.weeklyActiveUsers}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Growth Trajectory */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4" />
              Growth Trajectory
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Content</span>
                  <p className="font-medium">{insights?.totalGenerations}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Public Share</span>
                  <p className="font-medium">{insights?.publicRatio.toFixed(0)}%</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Monthly growth: {((insights?.recentGenerations / insights?.totalGenerations) * 100).toFixed(1)}%
              </div>
              <Badge variant="default">
                📈 Growing Platform
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Insights Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Platform Intelligence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">🎯 Focus Areas</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Boost {insights?.engagementRate < 5 ? 'engagement rates' : 'content quality'}</li>
                <li>• Encourage {insights?.publicRatio < 20 ? 'public sharing' : 'user retention'}</li>
                <li>• Optimize for peak hour ({format(new Date().setHours(insights?.peakHour || 14), 'h a')})</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">🚀 Opportunities</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• {insights?.mostPopularType} content is trending</li>
                <li>• {insights?.weeklyActiveUsers} users highly engaged</li>
                <li>• {insights?.userActivationRate > 40 ? 'Strong' : 'Growing'} user base</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">📊 Performance</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• {insights?.avgViewsPerGeneration.toFixed(1)} avg views per content</li>
                <li>• {insights?.engagementRate.toFixed(1)}% engagement rate</li>
                <li>• {insights?.recentGenerations} new content this month</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">🎨 Content Mix</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• {insights?.totalGenerations} total pieces</li>
                <li>• {insights?.publicRatio.toFixed(0)}% public content</li>
                <li>• {insights?.avgDaysSinceCreation.toFixed(0)} days avg age</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DetailedInsights;